import React, { useState, useEffect, useRef } from 'react';
import StatisticsEffectBg from '../../components/statistic-effect-bg';
import { useLanguage } from '../../helpers/language-context';
import { renderHTML } from '../../helpers/render-html';

const Counter = ({ value, duration = 4000, trigger }) => {
  const [count, setCount] = useState(0);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (!trigger) return;

    // Handle string values like "10M"
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const progress = timestamp - startTimeRef.current;
      const percentage = Math.min(progress / duration, 1);

      // Easing function: easeOutExpo
      const easing = percentage === 1 ? 1 : 1 - Math.pow(2, -10 * percentage);
      const currentCount = Math.floor(easing * numericValue);

      setCount(currentCount);

      if (percentage < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(numericValue); // Ensure it ends at the exact value
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration, trigger]);

  const suffix = typeof value === 'string' ? value.replace(/[0-9.]/g, '') : '';
  return <>{count}{suffix}</>;
};

const Statistics = () => {
  const { translations } = useLanguage();
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
          setIsInView(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const currentSectionRef = sectionRef.current;
    if (currentSectionRef) {
      observer.observe(currentSectionRef);
    }

    return () => {
      if (currentSectionRef) {
        observer.unobserve(currentSectionRef);
      }
    };
  }, []);

  const stats = [
    { label: translations.statistics.yearsInBusiness, value: 18 },
    { label: translations.statistics.projectsCompleted, value: 370 },
    { label: translations.statistics.awardsWon, value: 40 },
    { label: translations.statistics.metricTons, value: "153M" },
  ];

  return (
    <StatisticsEffectBg waveSize="large">
      <div ref={sectionRef} className="w-full">
        <h1
          id="building-tomorrow"
          className={`text-3xl md:text-5xl lg:text-[80px] font-light text-white mb-10 md:mb-20 max-w-6xl transition-all duration-1000 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          dangerouslySetInnerHTML={renderHTML(translations.home.buildingTomorrowsInfrastructure)}
        />

        <p
          className={`text-md md:text-lg text-blue-100 mb-14 max-w-4xl space-y-6 leading-relaxed transition-all duration-1000 delay-300 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          dangerouslySetInnerHTML={renderHTML(translations.home.description)}
        />

        <div className={`flex flex-col lg:flex-row justify-between items-center gap-12 lg:gap-8 transition-all duration-1000 delay-500 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
          {stats.map((stat, index) => (
            <div key={index} className="text-center flex-1">
              <div className="text-5xl md:text-[80px] font-bold text-[#FDA729] mb-2">
                <Counter value={stat.value} trigger={isInView} />+
              </div>
              <div className="text-lg text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </StatisticsEffectBg>
  );
};

export default Statistics;
